/**
 * CorrectionCapBanner — shows an amber warning as the envelope approaches the
 * per-envelope correction cap, and a red block once it's reached.
 *
 * Cap source: `/v3/config/signing-rules` → `maxCorrectionsPerEnvelope`. If the
 * config route 503s (unseeded) the banner renders "—" for the cap total and
 * does NOT block submission — matching the "config-driven, degrade gracefully"
 * rule from SOLUTION-BIBLE §Config.
 *
 * The parent component owns the `count` and the `atCap` derivation; this
 * component is pure presentation with one optional callback for "contact
 * Supervisor / create recovery task".
 */

import { AlertTriangle, Info } from '@retail-ops/panel-shell/fi';
import { Button } from '@/components/primitives';
import '../rsc.css';

export interface CorrectionCapBannerProps {
  /** Corrections already submitted for the scoped signer/envelope. */
  count: number;
  /**
   * Config cap (from `/v3/config/signing-rules.maxCorrectionsPerEnvelope`).
   * `undefined` means the config hasn't loaded yet (unseeded → render "—").
   */
  cap: number | undefined;
  /** Optional signer scope shown in the caption. */
  scopeLabel?: string;
  /**
   * Invoked at the cap — wires to SupervisorTask creation (out-of-band
   * signing-recovery). If omitted the button is hidden.
   */
  onContactSupervisor?: () => void;
}

export function CorrectionCapBanner(props: CorrectionCapBannerProps): JSX.Element | null {
  const { count, cap, scopeLabel, onContactSupervisor } = props;

  // Unseeded config — surface informational pill only.
  if (cap === undefined) {
    return (
      <div className="rsc-banner rsc-banner--info" role="status">
        <Info size={14} className="rsc-inline-icon" />
        <div>
          Corrections so far: <strong>{count}</strong> / <strong>—</strong>
          {scopeLabel ? <> · {scopeLabel}</> : null}. Cap config not yet seeded
          (<code>/v3/config/signing-rules</code>).
        </div>
      </div>
    );
  }

  const remaining = Math.max(cap - count, 0);
  const atCap = count >= cap;
  const warnZone = !atCap && remaining <= 1;

  if (atCap) {
    return (
      <div className="rsc-banner rsc-banner--err" role="alert">
        <AlertTriangle size={14} className="rsc-inline-icon" />
        <div style={{ flex: 1 }}>
          Correction cap reached (<strong>{count}/{cap}</strong>
          {scopeLabel ? <> · {scopeLabel}</> : null}). Further corrections must
          be handled by a Supervisor via an out-of-band signing-recovery task.
        </div>
        {onContactSupervisor && (
          <Button variant="danger" size="sm" onClick={onContactSupervisor}>
            Contact Supervisor
          </Button>
        )}
      </div>
    );
  }

  if (warnZone) {
    return (
      <div className="rsc-banner rsc-banner--warn" role="status">
        <AlertTriangle size={14} className="rsc-inline-icon" />
        <div>
          Last correction available: <strong>{count}/{cap}</strong>
          {scopeLabel ? <> · {scopeLabel}</> : null}. After this one a
          Supervisor override will be required.
        </div>
      </div>
    );
  }

  return (
    <div className="rsc-banner rsc-banner--info" role="status">
      <Info size={14} className="rsc-inline-icon" />
      <div>
        Corrections so far: <strong>{count}/{cap}</strong>
        {scopeLabel ? <> · {scopeLabel}</> : null}.
      </div>
    </div>
  );
}
